import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useDebounceCallback, type DebouncedState } from '@/hooks';

type UseDebounceValueOptions<T> = {
  leading?: boolean;
  trailing?: boolean;
  maxWait?: number;
  equalityFn?: (left: T, right: T) => boolean;
  onDebounce?: (text: T) => void;
};

export function useDebounceValue<T>(
  initialValue: T | (() => T),
  delay: number,
  options?: UseDebounceValueOptions<T>,
): [T, DebouncedState<(value: T) => void>] {
  const value =
    typeof initialValue === 'function'
      ? (initialValue as () => T)()
      : initialValue;

  const [debouncedValue, setDebouncedValue] = useState<T>(value as T);

  // Keep latest equality and onDebounce in refs so the debounced fn identity stays stable
  const eqRef = useRef<(l: T, r: T) => boolean>(
    options?.equalityFn ?? ((l: T, r: T) => l === r),
  );
  useEffect(() => {
    eqRef.current = options?.equalityFn ?? ((l: T, r: T) => l === r);
  }, [options?.equalityFn]);

  const onDebounceRef = useRef(options?.onDebounce);
  useEffect(() => {
    onDebounceRef.current = options?.onDebounce;
  }, [options?.onDebounce]);

  // Only pass timing options to debounce and keep them memoized
  const debounceOptions = useMemo(
    () => ({
      leading: options?.leading,
      trailing: options?.trailing ?? true,
      maxWait: options?.maxWait,
    }),
    [options?.leading, options?.trailing, options?.maxWait],
  );

  // Stable callback executed when debounce settles
  const settled = useCallback((text: T) => {
    setDebouncedValue(text);
    onDebounceRef.current?.(text);
  }, []);

  // Debounced setter with stable identity while delay/options unchanged
  const setDebounced = useDebounceCallback(settled, delay, debounceOptions);

  // Track previous raw value to apply equalityFn
  const prevRef = useRef<T>(value as T);

  // Schedule debounce when the input value changes (not during render)
  useEffect(() => {
    if (!eqRef.current(prevRef.current, value as T)) {
      prevRef.current = value as T;
      setDebounced(value as T);
    }
  }, [value, setDebounced]);

  // Cleanup any pending debounce on unmount
  useEffect(() => {
    return () => {
      setDebounced.cancel();
    };
  }, [setDebounced]);

  return [debouncedValue, setDebounced];
}
